import { Command as CommandPrimitive } from 'cmdk';
import { cn } from '../cn.js';
import { IconSearch } from '../icons.js';

/**
 * The command palette, on cmdk.
 *
 * It is the keyboard path to everything the workspace can do. The list is filtered as the person
 * types, and every item is reachable without a pointer.
 */
export function Command({ className, ...props }: React.ComponentProps<typeof CommandPrimitive>) {
  return (
    <CommandPrimitive
      className={cn(
        'flex w-full flex-col overflow-hidden',
        'rounded-(--radius-xl) bg-(--color-surface-elevated) text-(--color-text-primary)',
        'border border-(--color-border) shadow-(--shadow-overlay)',
        className,
      )}
      {...props}
    />
  );
}

export function CommandInput({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Input>) {
  return (
    <div className="flex items-center gap-(--space-sm) border-b border-(--color-border) px-(--space-md)">
      <IconSearch aria-hidden="true" className="size-(--size-icon-sm) opacity-(--opacity-muted)" />
      <CommandPrimitive.Input
        className={cn(
          'h-(--input-height-lg) w-full bg-transparent',
          'text-(length:--font-size-body-large) text-(--color-text-primary)',
          'placeholder:text-(--input-placeholder) outline-none',
          className,
        )}
        {...props}
      />
    </div>
  );
}

export function CommandList({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.List>) {
  return (
    <CommandPrimitive.List
      className={cn('max-h-80 overflow-y-auto p-(--space-xs)', className)}
      {...props}
    />
  );
}

export function CommandEmpty({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Empty>) {
  return (
    <CommandPrimitive.Empty
      className={cn(
        'py-(--space-xl) text-center text-(length:--font-size-body) text-(--color-text-muted)',
        className,
      )}
      {...props}
    />
  );
}

export function CommandGroup({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Group>) {
  return (
    <CommandPrimitive.Group
      className={cn(
        'px-(--space-xs) py-(--space-xs)',
        '[&_[cmdk-group-heading]]:px-(--space-sm) [&_[cmdk-group-heading]]:py-(--space-xs)',
        '[&_[cmdk-group-heading]]:text-(length:--font-size-caption)',
        '[&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-(--color-text-muted)',
        className,
      )}
      {...props}
    />
  );
}

export function CommandItem({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Item>) {
  return (
    <CommandPrimitive.Item
      className={cn(
        'flex cursor-pointer items-center gap-(--space-sm)',
        'rounded-(--radius-md) px-(--space-sm) py-(--space-xs)',
        'text-(length:--font-size-body)',
        'data-[selected=true]:bg-(--color-accent-subtle) data-[selected=true]:text-(--color-accent)',
        'data-[disabled=true]:opacity-(--opacity-disabled) data-[disabled=true]:cursor-not-allowed',
        className,
      )}
      {...props}
    />
  );
}

export function CommandSeparator({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Separator>) {
  return (
    <CommandPrimitive.Separator
      className={cn('my-(--space-xs) h-px bg-(--color-border)', className)}
      {...props}
    />
  );
}
